import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lightbulb } from "lucide-react";
import api from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";
import { hasPermission } from "../../lib/permissions";
import { PRODUCT_CREATE_KEYS, PRODUCT_READ_KEYS, deliveredInText } from "../../lib/product";
import { StatusBadge } from "./ProductBits";
import { Button } from "../ui/button";
import { Card } from "../ui/card";

// « Suites produit » on a ticket page (D-13): the product items linked to this ticket, and the way to report one. Nothing is shown to
// someone without a product right, and nothing is ever written to the ticket.
export default function TicketProductCard({ ticketId }) {
  const { user } = useAuth();
  const enabled = !!user?.features?.product;
  const canRead = enabled && hasPermission(user, PRODUCT_READ_KEYS);
  const canReport = enabled && hasPermission(user, PRODUCT_CREATE_KEYS);
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!canRead) return;
    api.get(`/product/for-ticket/${ticketId}`).then(({ data }) => setItems(data.items)).catch(() => setItems([]));
  }, [ticketId, canRead]);

  if (!canRead && !canReport) return null;
  return (
    <Card className="p-5 space-y-2" data-testid="ticket-product-card">
      <h2 className="text-sm font-semibold text-slate-900 flex items-center gap-1.5"><Lightbulb size={14} className="text-slate-400" />Suites produit</h2>
      {canRead && items && items.length === 0 && <p className="text-sm text-slate-500">Rien n'a été signalé au produit pour ce ticket.</p>}
      {canRead && items && items.length > 0 && (
        <ul className="space-y-2">
          {items.map((i) => (
            <li key={i.id} className="space-y-0.5" data-testid={`ticket-product-item-${i.number}`}>
              <Link to={`/produit/${i.id}`} className="text-sm hover:underline break-words"><span className="text-xs text-slate-500 tabular-nums">{i.number}</span> {i.title}</Link>
              <div className="flex flex-wrap items-center gap-1.5">
                <StatusBadge status={i.status} />
                {i.followed_in && <span className="text-xs text-slate-500">suivi dans {i.followed_in.number}</span>}
              </div>
              {(i.delivered_in?.length > 0 || i.followed_in?.delivered_in?.length > 0) && (
                <p className="text-xs text-emerald-800" data-testid={`ticket-product-delivered-${i.number}`}>
                  Correctif livré · {deliveredInText(i.delivered_in?.length ? i.delivered_in : i.followed_in.delivered_in).replace("Livré dans ", "version ")}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}
      {canReport && (
        <Button asChild variant="outline" size="sm" className="w-full sm:w-auto" data-testid="ticket-product-report">
          <Link to={`/produit/nouveau?ticket=${ticketId}`}>Signaler au produit</Link>
        </Button>
      )}
    </Card>
  );
}
